import { Controller } from "@hotwired/stimulus";
import { useDebounce } from "stimulus-use";
import { formatPrice } from "../helpers";

export default class extends Controller {
  static targets = ["comparePrice", "quantity"];
  static values = {
    variant: { type: String, default: "" },
    product: { type: String, default: "" },
    sample: { type: Boolean, default: false },
  };
  // Debounce quantity change to prevent multiple rapid fire requests.
  static debounces = ["updateQuantity"];

  connect() {
    useDebounce(this, { wait: 500 });
  }

  async initialize() {
    const res = await fetch(
      `/products/${this.element.dataset.handle}?view=json`
    );

    if (res.ok) {
      const data = await res.json();
      const targetVariant =
        this.element.dataset.lineItemVariantValue.split(":")[0];
      const variant = data.product.variants.find(
        (variant) => variant.id === parseInt(targetVariant)
      );

      if (!variant) return;
      const { price, compare_at_price } = variant;
      this.comparePrice = compare_at_price;

      if (price < compare_at_price) {
        if (this.hasComparePriceTarget) {
          this.comparePriceTarget.classList.remove("hidden");
          this.comparePriceTarget.innerText = formatPrice(
            compare_at_price * this.quantityTarget.value
          );
        }
      }

      const variantMeta = data.variants_meta.find(
        (_variant) => _variant.id == variant.id
      );

      if (variantMeta) {
        if (variantMeta.inventory_policy === "continue") {
          this.quantityTarget.removeAttribute("max");
        }
      }
    }
  }

  async handleRemoveItem(e) {
    e.preventDefault();
    const data = await this.removeItem();

    if (data) {
      this.element.remove();

      if (this.quickCartController) {
        this.quickCartController.updateSubtotal(data);
      }
    }
  }

  handleUpdateQuantity(e) {
    const direction = e.params.direction;
    const quantity = parseInt(this.quantityTarget.value);

    if (direction === "increase") {
      if (
        !this.quantityTarget.max ||
        quantity < parseInt(this.quantityTarget.max)
      ) {
        this.quantityTarget.value = quantity + 1;
        this.updateQuantity(quantity + 1);
      }
    } else if (direction === "decrease") {
      if (quantity > 1) {
        this.quantityTarget.value = quantity - 1;
        this.updateQuantity(quantity - 1);
      }
    }
  }

  async handleQuantityRequest(id, quantity, isSample) {
    let quantityDelta = quantity;
    const samples = JSON.parse(document.body.dataset.samples);
    if (isSample) {
      const limit = 3;
      const exceedsItemLimit = isSample && quantity > limit;
      const restSamples = samples.filter((s) => s.id !== id.split(":")[0]);

      quantityDelta = Math.min(quantity, limit);
      if (exceedsItemLimit) {
        this.quantityTarget.value = limit;
        // alert("Limit of 3 samples per item");
      }

      const restSampleCount = restSamples
        .map((s) => s.quantity)
        .reduce((a, b) => a + b, 0);

      const sampleTotal = restSampleCount + parseInt(this.quantityTarget.value);

      if (sampleTotal > 40) {
        const diff = Math.max(40 - restSampleCount, 0);
        quantityDelta = Math.min(diff, this.quantityTarget.value);
        this.quantityTarget.value = quantityDelta;

        alert("You can only purchase 40 samples per order");
      }
    }

    try {
      const res = await fetch("/cart/change.js", {
        method: "post",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id,
          quantity: quantityDelta,
        }),
      });

      if (res.ok) {
        if (isSample) {
          const index = samples.findIndex(
            (sample) => id.split(":")[0] === sample.id
          );
          samples[index].quantity = quantityDelta;

          document.body.dataset.samples = JSON.stringify(samples);
        }
        const data = await res.json();

        return data;
      } else {
        console.error("Couldn't update quantity", res);
        return res;
      }
    } catch (error) {
      console.error("Couldn't update quantity", error);
    }
  }

  async removeItem() {
    const res = await this.handleQuantityRequest(this.variantValue, 0);

    if (res?.item_count === 0) {
      this.quickCartController.handleEmptyCart();
    }

    this.quickCartController.basketTargets.forEach((target) => {
      target.innerText = res.item_count;
    });

    return res;
  }

  async updateQuantity(quantity) {
    const data = await this.handleQuantityRequest(
      this.variantValue,
      quantity,
      this.sampleValue
    );

    if (data) {
      this.updateItem(data);
      this.quickCartController.basketTargets.forEach((target) => {
        target.innerText = data.item_count;
      });
      this.quickCartController.updateSubtotal(data);
    }
  }

  updateItem(data) {
    const price = this.element.querySelector(".line-item__price");
    const comparePrice = this.element.querySelector(
      ".line-item__price-compare"
    );

    const item = data.items.find((item) => {
      return (
        item.variant_id === this.variantValue ||
        item.id == this.variantValue.split(":")[0]
      );
    });

    if (item) {
      this.quantityTarget.value = item.quantity;

      if (comparePrice) {
        comparePrice.innerText = formatPrice(
          item.original_line_price,
          true,
          false,
          false
        );
        if (item.final_line_price < item.original_line_price) {
          comparePrice.classList.remove("hidden");
        } else {
          comparePrice.classList.add("hidden");
        }
      }
      price.innerText = formatPrice(item.final_line_price, true, false, false);
    }
  }

  get quickCartController() {
    return this.application.controllers.find((controller) => {
      return controller.context.identifier === "quickcart";
    });
  }
}
